/**
 * Cliente HTTP contra la API propia.
 *
 * La sesión vive en cookies `httpOnly`: este código nunca ve un token, nunca lo
 * guarda y nunca lo manda en un encabezado. Todo lo que hace es pedir con
 * `credentials: 'same-origin'` y dejar que el navegador adjunte las cookies.
 * Es lo que hace imposible que un XSS se lleve la sesión.
 */
import type { CodigoError, RespuestaError } from '@compranorte/contracts'

/** Detalle de validación por campo, tal como lo manda el backend. */
export interface DetalleError {
  campo: string
  mensaje: string
}

/**
 * Error con la forma uniforme de la API ya parseada.
 *
 * `codigo` es lo que se mira para decidir qué hacer; `mensaje` es lo que se le
 * muestra a la persona (el backend ya lo escribe pensando en eso, y nunca trae
 * stack ni mensajes del motor de base de datos); `detalles` trae el error por
 * campo cuando la validación falló.
 */
export class ErrorApi extends Error {
  constructor(
    readonly codigo: CodigoError,
    mensaje: string,
    readonly estado: number,
    readonly detalles?: DetalleError[],
  ) {
    super(mensaje)
    this.name = 'ErrorApi'
  }

  /** Mensaje del campo indicado, si el backend lo detalló. */
  detalleDe(campo: string): string | undefined {
    return this.detalles?.find((d) => d.campo === campo)?.mensaje
  }
}

/**
 * Una cuenta suspendida responde 403 `sin_permiso`, igual que una ruta para la
 * que no se tiene rol. Se distinguen por el mensaje porque es la única señal
 * que da el backend, y hace falta distinguirlas: a quien fue suspendido hay que
 * decírselo, no dejarlo mirando una pantalla vacía como si no hubiera entrado.
 */
export function esSuspension(e: unknown): boolean {
  return e instanceof ErrorApi && e.estado === 403 && /suspendida/i.test(e.message)
}

export interface Opciones {
  metodo?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Se serializa a JSON. `undefined` no manda cuerpo. */
  cuerpo?: unknown
  senal?: AbortSignal
  /** Apaga el refresco transparente para esta petición. */
  sinRefresco?: boolean
}

/*
 * Rutas que NUNCA se reintentan tras un 401.
 *
 * `/refrescar` porque reintentarlo con su propio mecanismo sería una recursión;
 * `/entrar` porque su 401 significa "contraseña incorrecta", no "token vencido",
 * y refrescar ahí convertiría un error de credenciales en una llamada inútil que
 * además puede revocar la sesión que la persona ya tenía abierta.
 */
const SIN_REINTENTO = ['/api/auth/refrescar', '/api/auth/entrar']

/**
 * Candado del refresco concurrente.
 *
 * El backend rota el token de refresco: cada uso emite uno nuevo y marca el
 * anterior como consumido, y un segundo uso del mismo token se interpreta como
 * robo y **revoca la familia entera de sesiones**. Por eso N peticiones que
 * reciben 401 a la vez —cosa normalísima: una pantalla monta cuatro consultas
 * de golpe— no pueden disparar N refrescos: todas menos la primera llegarían
 * con el token ya consumido y cerrarían la sesión del usuario legítimo.
 *
 * La solución es una única promesa compartida. La primera petición que necesita
 * refrescar la crea; las demás la encuentran ocupada y esperan SU resultado en
 * vez de lanzar otro refresco. Se limpia al asentarse, de modo que un 401
 * posterior (con las cookies ya rotadas) sí pueda intentar de nuevo.
 */
let refrescoEnVuelo: Promise<boolean> | null = null

async function ejecutarRefresco(): Promise<boolean> {
  try {
    const res = await fetch('/api/auth/refrescar', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    return res.ok
  } catch {
    // Un fallo de red no es una sesión inválida, pero acá da lo mismo: no se
    // pudo renovar, así que el 401 original se propaga tal cual.
    return false
  }
}

function refrescarUnaVez(): Promise<boolean> {
  refrescoEnVuelo ??= ejecutarRefresco().finally(() => {
    refrescoEnVuelo = null
  })
  return refrescoEnVuelo
}

function enviar(ruta: string, o: Opciones): Promise<Response> {
  const cabeceras: Record<string, string> = { Accept: 'application/json' }
  if (o.cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json'

  return fetch(ruta, {
    method: o.metodo ?? (o.cuerpo !== undefined ? 'POST' : 'GET'),
    credentials: 'same-origin',
    headers: cabeceras,
    // El cuerpo se serializa acá y no antes porque el reintento vuelve a
    // llamar a `enviar`: si fuera un stream, la segunda vez llegaría vacío.
    body: o.cuerpo === undefined ? undefined : JSON.stringify(o.cuerpo),
    signal: o.senal,
  })
}

async function leer<T>(res: Response): Promise<T> {
  // 204 es la respuesta de `salir` y `refrescar`: no hay cuerpo que parsear.
  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T
  }

  const texto = await res.text()
  let cuerpo: unknown = null
  if (texto) {
    try {
      cuerpo = JSON.parse(texto)
    } catch {
      cuerpo = null
    }
  }

  if (!res.ok) {
    const forma = cuerpo as RespuestaError | null
    if (forma?.error?.codigo) {
      throw new ErrorApi(forma.error.codigo, forma.error.mensaje, res.status, forma.error.detalles)
    }
    /*
     * Sin forma uniforme el fallo no viene de la aplicación: es el proxy, la
     * pantalla de un balanceador o una caída. Se traduce a `no_disponible` para
     * que la interfaz no tenga que distinguir dos formas de error.
     */
    throw new ErrorApi(
      'no_disponible',
      res.status >= 500
        ? 'El servidor no está respondiendo. Intente en un momento.'
        : `La petición falló (HTTP ${res.status}).`,
      res.status,
    )
  }

  return cuerpo as T
}

/**
 * Hace la petición y devuelve el cuerpo ya parseado, o lanza `ErrorApi`.
 *
 * Ante un 401 intenta refrescar la sesión UNA vez y repite la petición
 * original. Es transparente a propósito: quien vuelve al día siguiente con la
 * cookie de acceso vencida pero la de refresco viva no debería ver una pantalla
 * de login, y menos perder lo que tenía escrito en un formulario.
 */
export async function pedir<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const res = await enviar(ruta, opciones)

  const reintentable =
    res.status === 401 && !opciones.sinRefresco && !SIN_REINTENTO.some((r) => ruta.startsWith(r))

  if (reintentable && (await refrescarUnaVez())) {
    return leer<T>(await enviar(ruta, opciones))
  }

  return leer<T>(res)
}
